import {
  MinisterialActivityActionDefinition,
  MinistryEventType,
} from '../../models/person-event.model';

export type MinisterialActivityActionGroup = 'ordination' | 'change' | 'temporary' | 'ending';

export interface MinisterialActivityActionPresentation {
  eventType: MinistryEventType;
  label: string;
  description: string;
  designationLabel?: string;
  group: MinisterialActivityActionGroup;
  icon: string;
}

export const hiddenMinisterialActivityActions = new Set<MinistryEventType>([
  'ApostleAreaTransferIn',
  'ApostleAreaTransferOut',
  'FormerMinisterAsOfficiant',
]);

export const ministerialActivityActionOrder: MinistryEventType[] = [
  'MinisterialInstitution', // Institucion Ministerial
  'AppointmentAssignment', // Encargos / Nombramientos
  'NewOfficiant', // Oficiante
  'Reassignment', // Pase / reasignacion de area
  'Confirmation', // Confirmacion en el ministerio
  'Reinstatement', // Reintegro luego de una licencia
  'LeaveOfAbsence', // Licencia
  'MinisterialActivityExtension', // Extension de la actividad ministerial
  'MinisterialLicenseRenewal', // Renovacion de licencia ministerial
  'AppointmentEnd', // Desencargo
  'Retirement', // Puesta en descanso
  'Dismissal', // Relevo
  'Resignation', // Renuncia
  'Death', // Fallecimiento
];

export const ministerialActivityActionPresentation: Record<string, MinisterialActivityActionPresentation> = {
  AppointmentAssignment: {
    eventType: 'AppointmentAssignment',
    label: 'Encargo / Nombramiento',
    description: 'Asigna un nuevo encargo o nombramiento para un área de actividad',
    designationLabel: 'Encargo/Nombramiento',
    group: 'ordination',
    icon: 'assignment_ind',
  },
  MinisterialInstitution: {
    eventType: 'MinisterialInstitution',
    label: 'Institución Ministerial',
    description: 'Asigna o modifica el ministerio del miembro para un área de actividad',
    designationLabel: 'Ministerio',
    group: 'ordination',
    icon: 'volunteer_activism',
  },
  NewOfficiant: {
    eventType: 'NewOfficiant',
    label: 'Oficiante',
    description: 'Registra al miembro como oficiante',
    group: 'ordination',
    icon: 'volunteer_activism',
  },
  Reassignment: {
    eventType: 'Reassignment',
    label: 'Pase / reasignación de área',
    description: 'Modifica el área de una designación activa',
    group: 'change',
    icon: 'swap_horiz',
  },
  ApostleAreaTransferIn: {
    eventType: 'ApostleAreaTransferIn',
    label: 'Pase / reasignación de área',
    description: 'Modifica el área de una designación activa',
    group: 'change',
    icon: 'swap_horiz',
  },
  ApostleAreaTransferOut: {
    eventType: 'ApostleAreaTransferOut',
    label: 'Pase / reasignación de área',
    description: 'Modifica el área de una designación activa',
    group: 'change',
    icon: 'swap_horiz',
  },
  MinisterialActivityExtension: {
    eventType: 'MinisterialActivityExtension',
    label: 'Extension de la actividad ministerial',
    description: 'Actualiza la fecha de finalizacion de la actividad',
    group: 'temporary',
    icon: 'event_repeat',
  },
  MinisterialLicenseRenewal: {
    eventType: 'MinisterialLicenseRenewal',
    label: 'Renovacion de licencia ministerial',
    description: 'Actualiza la fecha de finalizacion de la licencia',
    group: 'temporary',
    icon: 'event_repeat',
  },
  Confirmation: {
    eventType: 'Confirmation',
    label: 'Confirmacion en el ministerio',
    description: 'Asigna un ministerio y area de actividad',
    designationLabel: 'Ministerio',
    group: 'change',
    icon: 'volunteer_activism',
  },
  Reinstatement: {
    eventType: 'Reinstatement',
    label: 'Reintegro luego de una licencia',
    description: 'Cambia el ministerio a estado activo y asigna un area de actividad',
    group: 'change',
    icon: 'play_circle',
  },
  LeaveOfAbsence: {
    eventType: 'LeaveOfAbsence',
    label: 'Licencia ministerial',
    description: 'El ministerio pasa a licencia y se finalizan las designaciones activas',
    group: 'temporary',
    icon: 'pause_circle',
  },
  AppointmentEnd: {
    eventType: 'AppointmentEnd',
    label: 'Desencargo',
    description: 'Finaliza un encargo/nombramiento',
    designationLabel: 'Encargo/Nombramiento',
    group: 'temporary',
    icon: 'event_busy',
  },
  Dismissal: {
    eventType: 'Dismissal',
    label: 'Relevacion Ministerial',
    description: 'Finaliza el ministerio por relevo',
    group: 'ending',
    icon: 'block',
  },
  Resignation: {
    eventType: 'Resignation',
    label: 'Renuncia',
    description: 'Finaliza el ministerio por renuncia',
    group: 'ending',
    icon: 'assignment_return',
  },
  Retirement: {
    eventType: 'Retirement',
    label: 'Puesta en descanso',
    description: 'Cambia el ministerio a estado de descanso y finaliza las designaciones activas',
    group: 'temporary',
    icon: 'event_busy',
  },
  Death: {
    eventType: 'Death',
    label: 'Fallecimiento',
    description: 'Finaliza el ministerio por fallecimiento',
    group: 'ending',
    icon: 'block',
  },
};

export function actionPresentationFor(
  definition: MinisterialActivityActionDefinition,
): MinisterialActivityActionPresentation {
  return ministerialActivityActionPresentation[definition.eventType] ?? {
    eventType: definition.eventType,
    label: definition.eventType,
    description: '',
    group: fallbackActionGroup(definition),
    icon: fallbackActionIcon(definition),
  };
}

export function compareMinisterialActivityActions(
  left: MinistryEventType,
  right: MinistryEventType,
): number {
  return actionOrderIndex(left) - actionOrderIndex(right);
}

function actionOrderIndex(eventType: MinistryEventType): number {
  const index = ministerialActivityActionOrder.indexOf(eventType);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function fallbackActionIcon(definition: MinisterialActivityActionDefinition): string {
  if (definition.requiresExpectedEndDate) {
    return 'event_repeat';
  }
  if (definition.requiresAppointmentOrAssignment) {
    return definition.eventType === 'AppointmentEnd' ? 'event_busy' : 'assignment_ind';
  }
  if (definition.targetStatus === 'Terminated') {
    return 'block';
  }
  if (definition.targetStatus === 'Retired') {
    return 'event_busy';
  }
  if (definition.targetStatus === 'Licensed') {
    return 'pause_circle';
  }
  return 'volunteer_activism';
}

function fallbackActionGroup(definition: MinisterialActivityActionDefinition): MinisterialActivityActionGroup {
  if (definition.requiresExpectedEndDate ||
      definition.targetStatus === 'Retired' ||
      definition.targetStatus === 'Licensed' ||
      definition.eventType === 'AppointmentEnd') {
    return 'temporary';
  }

  if (definition.targetStatus === 'Terminated' ||
      definition.eventType === 'Death') {
    return 'ending';
  }

  return definition.requiresMinistry || definition.requiresAppointmentOrAssignment
    ? 'ordination'
    : 'change';
}
